import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AiSession } from "../types/ai-session.entity";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Amount } from "src/components/Amount";
import { useGetAISessionScenariosByIdsQuery } from "../ai-sessions.service";
import {
  getAISessionContext,
  getAISessionContextMode,
  getAISessionContextSummary,
  getAISessionLanguage,
  getTopicContextCustomScenarioIds,
} from "../utils";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type Props = { data: (AiSession & { id: string })[] };

const statusClass = (status: AiSession["status"]) => {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "COMPLETED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    default:
      return "bg-amber-100 text-amber-700 border-amber-200";
  }
};

const contextClass = (mode: AiSession["context"]["sessionMode"]) => {
  switch (mode) {
    case "TOPIC":
      return "bg-purple-50 text-purple-700 border-purple-200";
    case "LEARNING_TRACK":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "WORD_BANK":
      return "bg-cyan-50 text-cyan-700 border-cyan-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

const formatDate = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleDateString();
};

const formatTime = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (minutes?: number) => {
  if (!minutes) return "N/A";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatValue = (value?: string | null) => value ?? "None";

export const AISessionsTable: React.FC<Props> = ({ data }) => {
  const { search } = useLocation();
  const scenarioIds = React.useMemo(() => {
    return Array.from(
      new Set(
        data.flatMap((session) => {
          const context = getAISessionContext(session);
          return context.sessionMode === "TOPIC"
            ? getTopicContextCustomScenarioIds(context.items)
            : [];
        })
      )
    );
  }, [data]);

  const { data: scenarios = [] } = useGetAISessionScenariosByIdsQuery(
    { scenarioIds },
    { skip: scenarioIds.length === 0 }
  );

  const scenarioById = React.useMemo(
    () => new Map(scenarios.map((scenario) => [scenario.id, scenario] as const)),
    [scenarios]
  );

  const contextSummaryBySessionId = React.useMemo(() => {
    return new Map(
      data.map((session) => [
        session.id,
        getAISessionContextSummary(session, { scenarioById }),
      ])
    );
  }, [data, scenarioById]);

  const contextModeBySessionId = React.useMemo(() => {
    return new Map(data.map((session) => [session.id, getAISessionContextMode(session)]));
  }, [data]);

  const languageBySessionId = React.useMemo(() => {
    return new Map(data.map((session) => [session.id, getAISessionLanguage(session)]));
  }, [data]);

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[1500px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Session</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">User</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Context</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Language</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Duration</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Amount</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Started</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center py-4 text-xl text-gray-500">No AI Sessions</td>
            </tr>
          )}
          {data.map((session) => {
            const contextSummary = contextSummaryBySessionId.get(session.id) ?? "None";
            const contextMode = contextModeBySessionId.get(session.id) ?? "TOPIC";
            const language = languageBySessionId.get(session.id) ?? null;
            return (
            <tr key={session.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200 font-mono text-[11px] sm:text-xs" title={session.id}>
                {session.id}
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                {session.userId ? (
                  <Link
                    to={`/users/${session.userId}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                  >
                    {session.userId}
                  </Link>
                ) : (
                  <span className="text-gray-500">No User ID</span>
                )}
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className={`px-2 py-1 rounded-full border text-xs ${statusClass(session.status)}`}>{session.status}</span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex items-center justify-center p-1">
                  <span
                    className={`px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-semibold ${contextClass(contextMode)}`}
                    title={`${contextMode}: ${contextSummary}`}
                  >
                    {contextSummary}
                  </span>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex items-center justify-center p-1">
                  <span
                    className="px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-semibold bg-cyan-100 text-cyan-700 border-cyan-200 whitespace-nowrap"
                    title={formatValue(language)}
                  >
                    {formatValue(language)}
                  </span>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <span className="text-[10px] sm:text-[11px] font-semibold text-amber-700">
                  {formatDuration(session.durationInMins ?? undefined)}
                </span>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                {session.deductedMoney ? (
                  <Amount amount={session.deductedMoney.amount} currency={session.deductedMoney.currency} />
                ) : (
                  <span className="text-[10px] sm:text-[11px] text-gray-400">N/A</span>
                )}
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{formatDate(session.startTime)}</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">{formatTime(session.startTime)}</span>
                </div>
              </td>
              <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                <Link
                  to={appendSearchToPath(`/ai-sessions/${session.id}`, search)}
                  className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 no-underline"
                >
                  <FaExternalLinkAlt />
                </Link>
              </td>
            </tr>
          )})}
        </tbody>
      </table>
    </div>
  );
};

export default AISessionsTable;
